import React from "react";
import {
    Container,
    SelectedAddress,
    Title,
    Description,
    DetectLocationContainer,
    MapWrapper,
    InputContainer,
    Input,
    MapMarker,
    ProgressBarContainer,
    ProgressBarContainerBlock,
    CurrentLocationText,
    ChangeButton
  } from "./styles";
  import { Flex } from "@acko-tech/building-blocks.ui.common";
  import GoogleMapReact from "google-map-react";
  import Icon from "@widgets/Icon";
  import { useSelector } from "react-redux";
  import { useState, useEffect, useRef } from "react";
  import { useLocationPicker } from "@utils/hooks/useLocationPicker";
  import { reverseGeoCode } from "@utils/geo";
  
  const ProgressBar = ({ start, defaultTimer = 5000, end }: any) => {
    const [completed, setCompleted] = useState(false);
    const [width, setWidth] = useState(0);
    const intervalRef: any = useRef(null);
  
    const startBarAnimation = () => {
      setCompleted(false);
      const elem = document.getElementById("progress-bar-container-progress");
  
      if (!elem) return;
  
      elem.style.width = "0%";
  
      const intervalTime = defaultTimer / 10;
  
      intervalRef.current = setInterval(() => {
        setWidth((prev) => {
          if (prev === 70) clearInterval(intervalRef.current);
          elem.style.width = `${prev + 10}%`;
          return prev + 10;
        });
      }, intervalTime);
    };
  
    const endBarAnimation = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const elem = document.getElementById("progress-bar-container");
  
      if (!elem) return;
      elem.style.width = "100%";
      setTimeout(() => {
        setCompleted(true);
      }, 1000);
    };
  
    useEffect(() => {
      if (start) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setWidth(0);
        startBarAnimation();
      }
  
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, [start]);
  
    useEffect(() => {
      if (end) {
        endBarAnimation();
      }
    }, [end]);
  
    if (completed) return <></>;
    return (
      <ProgressBarContainer id="progress-bar-container">
        <ProgressBarContainerBlock id="progress-bar-container-progress" />
      </ProgressBarContainer>
    );
  };
  
  const GoogleMap = ({ is_visible, actions }: any) => {
    const { lat, lng, mainText, secondaryText } = 
    useSelector(({ globalState }: any) => {
      return {
        lat: globalState?.payloadData?.address?.lat,
        lng: globalState?.payloadData?.address?.lng,
        mainText: globalState?.payloadData?.address?.mainText,
        secondaryText: globalState?.payloadData?.address?.secondaryText
      }
    })

    const [currentPosition, setCurrentPosition] = useState({ lat, lng });
    const {
      getCurrentLocation,
      position,
      fetchingLocation,
      geoError,
    //   locationFetched,
    //   permission,
    } = useLocationPicker();
  
    useEffect(() => {
      if (!fetchingLocation && !geoError && position.lat && position.lng) {
        setCurrentPosition(position);
        getAddressFromPosition(position);
      }
    }, [position, geoError, fetchingLocation]);
  
    useEffect(() => {
      actions?.on_location_fetching?.forEach((func: any) => {
        func.funCall?.({
          ...func.args,
          value: fetchingLocation,
        });
      });
    }, [fetchingLocation]);
  
    const getAddressFromPosition = async ({ lat, lng }: any) => {
      const response = await reverseGeoCode(lat, lng);
  
      if (!response.error) {
        const data = {
          mainText: response.mainText,
          secondaryText: response.secondaryText,
          fullAddress: response.address,
          placeId: "",
          postalCode: response.postalCode,
          lat,
          lng,
          city: response.city,
          state: response.state
        }
        actions?.on_location_click?.forEach((eachFunc: any) => {
          eachFunc.funCall({
            payloadData: {
              address: data,
              pincode: response.postalCode
            },
            ...eachFunc.args
          });
        });
      }
    };
  
    const createMapOptions = () => ({
      gestureHandling: "greedy",
      fullscreenControl: false,
    });
  
    const handleCenterChanged = (map: any) => {
      const center = map.getCenter();
      const lat = center.lat();
      const lng = center.lng();
      setCurrentPosition({ lat, lng });
      getAddressFromPosition({ lat, lng });
    };
  
    const inputHandler = () => {
      actions?.on_input_click?.forEach((func: any) => {
        func.funCall?.(func.args);
      });
    };

    const changeHandler = () => {
      actions?.on_change_click?.forEach((func: any) => {
        func.funCall?.(func.args);
      });
    };
  
    if (!is_visible) return <></>;
  
    return (
      <Container>
        <MapWrapper>
          <GoogleMapReact
            bootstrapURLKeys={{
              key: process.env.NEXT_PUBLIC_GMAPS_API_KEY,
              language: "en",
              libraries: ["places", "geometry", "drawing"],
            }}
            defaultCenter={{
              lat: currentPosition.lat,
              lng: currentPosition.lng,
            }}
            center={[currentPosition.lat, currentPosition.lng]}
            defaultZoom={16}
            onDragEnd={handleCenterChanged}
            options={createMapOptions}
            onGoogleApiLoaded={() => {}}
            yesIWantToUseGoogleMapApiInternals={true}
          />
          <InputContainer onClick={inputHandler}>
            <Input placeholder="Try JP nagar, Andheri west etc.." />
            <Icon
              overrideIsVisible={true}
              path={"/images/magnifier_search.svg"}
              styles={{ position: "absolute", top: "15px", right: "18px" }}
            />
          </InputContainer>
          <DetectLocationContainer onClick={getCurrentLocation}>
            <Icon
              overrideIsVisible={true}
              styles={{ marginRight: "10px" }}
              path={"/images/GPS.svg"}
            />
            <CurrentLocationText>Use current location</CurrentLocationText>
          </DetectLocationContainer>
          <MapMarker>
            <Icon overrideIsVisible={true} path={"/images/location.svg"} />
          </MapMarker>
        </MapWrapper>
        <SelectedAddress>
          {fetchingLocation ? <ProgressBar start={fetchingLocation} /> : ""}
          <Flex alignItems="flex-start" justifyContent="space-between">
            <div>
              <Title style={{ marginBottom: "4px" }}>{mainText}</Title>
              <Description>{secondaryText}</Description>
            </div>
            <ChangeButton onClick={changeHandler}>Change</ChangeButton>
          </Flex>
        </SelectedAddress>
      </Container>
    );
  };
  
  export default GoogleMap;
  